import { useState, useEffect, useRef } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import toast from "react-hot-toast";
import { HiOutlineSearch } from "react-icons/hi";
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  size,
  useDismiss,
  useRole,
  useInteractions,
  FloatingPortal,
} from "@floating-ui/react";
import { useQuery, keepPreviousData } from "@tanstack/react-query";

import API from "../api";
import { capture } from "../instrument";
import { isRequiredFieldState } from "../utils";

const MIN_QUERY_LENGTH = 2;
const DEBOUNCE_MS = 200;
const STALE_TIME_MS = 60_000;
const POPOVER_Z_INDEX = 60;

const MutuelleLookup = ({ initialMutuelle = undefined, onMutuelleSelect, onMutuelleRemove, placeholder = "Mutuelle", fieldState = undefined }) => {
  const [selectedMutuelle, setSelectedMutuelle] = useState(null);
  const [inputValue, setInputValue] = useState("");
  const [debouncedInputValue, setDebouncedInputValue] = useState("");
  const [isFocused, setIsFocused] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [userDismissed, setUserDismissed] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [isKeyboardNavigating, setIsKeyboardNavigating] = useState(false);

  const inputRef = useRef(null);
  const selectedItemRef = useRef(null);

  useEffect(() => {
    if (!isEditing) return;
    inputRef.current?.focus();
    inputRef.current?.select?.();
  }, [isEditing]);

  useEffect(() => {
    if (!initialMutuelle?.code && !initialMutuelle?.name) return;
    setSelectedMutuelle(initialMutuelle);
  }, [initialMutuelle?.code, initialMutuelle?.name, initialMutuelle?.manual, initialMutuelle?.adherentNumber, initialMutuelle?.rightsFrom, initialMutuelle?.rightsTo]);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedInputValue(inputValue), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [inputValue]);

  const normalizedQuery = debouncedInputValue.trim();
  const queryEnabled = normalizedQuery.length >= MIN_QUERY_LENGTH;

  const { data, isFetching, error } = useQuery({
    queryKey: ["mutuelle", normalizedQuery],
    queryFn: async ({ signal }) => {
      const response = await API.get(`/mutuelle?q=${encodeURIComponent(normalizedQuery)}`, { signal });
      if (!response.ok) {
        if (response.message) toast.error(response.message);
        return [];
      }
      return response.data || [];
    },
    enabled: queryEnabled,
    retry: false,
    staleTime: STALE_TIME_MS,
    placeholderData: keepPreviousData,
  });

  const suggestions = queryEnabled ? data || [] : [];
  const isLoading = queryEnabled && isFetching;

  useEffect(() => {
    if (!error) return;
    if (error?.name === "AbortError") return;
    capture(error);
    toast.error("Une erreur est survenue lors de la recherche de la mutuelle");
  }, [error]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [normalizedQuery]);

  useEffect(() => {
    if (selectedItemRef.current) selectedItemRef.current.scrollIntoView({ block: "nearest" });
  }, [selectedIndex]);

  const hasQuery = inputValue.trim().length >= MIN_QUERY_LENGTH;
  const showPopover = isFocused && hasQuery && !userDismissed;

  const { refs, floatingStyles, context } = useFloating({
    open: showPopover,
    onOpenChange: (next) => {
      if (next === false) setUserDismissed(true);
    },
    placement: "bottom-start",
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(0),
      flip({ padding: 8 }),
      shift({ padding: 8 }),
      size({
        padding: 8,
        apply({ rects, availableHeight, elements }) {
          Object.assign(elements.floating.style, {
            width: `${rects.reference.width}px`,
            maxHeight: `${availableHeight}px`,
          });
        },
      }),
    ],
  });

  const dismiss = useDismiss(context);
  const role = useRole(context, { role: "dialog" });
  const { getReferenceProps, getFloatingProps } = useInteractions([dismiss, role]);

  const normalized = inputValue.trim().toLowerCase();
  const hasExactMatch = suggestions.some((s) => (s.code || "").toLowerCase() === normalized);
  const footerVisible = normalized.length >= MIN_QUERY_LENGTH && hasExactMatch === false;
  const totalItems = suggestions.length + (footerVisible ? 1 : 0);
  const footerSelected = footerVisible && selectedIndex === suggestions.length;

  const selectMutuelle = (mutuelle) => {
    setSelectedMutuelle(mutuelle);
    onMutuelleSelect(mutuelle);
    setInputValue("");
    setIsEditing(false);
    setUserDismissed(false);
    inputRef.current?.blur();
  };

  const addManualMutuelle = () => {
    const trimmed = inputValue.trim();
    if (!trimmed) return;
    selectMutuelle({ code: trimmed, name: "", manual: true });
  };

  const startEditing = () => {
    setInputValue(selectedMutuelle?.code || "");
    setUserDismissed(false);
    setIsEditing(true);
  };

  const stopEditing = () => {
    setIsEditing(false);
    setInputValue("");
  };

  const navOpts = { enabled: showPopover && totalItems > 0, enableOnFormTags: ["INPUT"], preventDefault: true };
  useHotkeys("down", () => { setIsKeyboardNavigating(true); setSelectedIndex((i) => Math.min(totalItems - 1, i + 1)); }, navOpts, [showPopover, totalItems]);
  useHotkeys("up", () => { setIsKeyboardNavigating(true); setSelectedIndex((i) => Math.max(0, i - 1)); }, navOpts, [showPopover, totalItems]);
  useHotkeys(
    "enter",
    () => {
      if (footerSelected === true) addManualMutuelle();
      else if (suggestions[selectedIndex]) selectMutuelle(suggestions[selectedIndex]);
    },
    navOpts,
    [showPopover, totalItems, footerSelected, suggestions, selectedIndex],
  );

  const onKeyDown = (event) => {
    if (event.key === "Escape" && isEditing === true) {
      event.preventDefault();
      stopEditing();
      inputRef.current?.blur();
    }
  };

  const onPanelMouseMove = () => {
    if (isKeyboardNavigating === true) setIsKeyboardNavigating(false);
  };

  const hasValue = Boolean(selectedMutuelle?.code);
  const isRequiredEmpty = isRequiredFieldState(fieldState) && hasValue === false;
  const chipLabelExtras = [
    selectedMutuelle?.name || null,
    selectedMutuelle?.manual === true ? "(manuel)" : null,
  ].filter(Boolean).join(" ");
  const displayValue = hasValue ? [selectedMutuelle.code, chipLabelExtras].filter(Boolean).join(" — ") : "";

  return (
    <div className="flex flex-1 flex-col gap-1">
      <div ref={refs.setReference} {...getReferenceProps()} className={`field-shell${showPopover ? " rounded-b-none" : ""}`} data-state={isRequiredEmpty ? "required" : undefined}>
        <input
          ref={inputRef}
          type="text"
          value={isEditing ? inputValue : displayValue}
          onChange={(event) => {
            setInputValue(event.target.value);
            setUserDismissed(false);
            if (selectedMutuelle !== null) {
              setSelectedMutuelle(null);
              onMutuelleRemove?.();
            }
          }}
          onKeyDown={onKeyDown}
          onFocus={() => {
            setIsFocused(true);
            setUserDismissed(false);
            if (isEditing === false) startEditing();
          }}
          onBlur={() =>
            setTimeout(() => {
              setIsFocused(false);
              if (isEditing === true) stopEditing();
            }, 150)
          }
          placeholder={placeholder}
          aria-label="Rechercher une mutuelle"
          className={`min-w-0 flex-1 bg-transparent text-sm text-gray-800 outline-none ${isRequiredEmpty ? "placeholder:text-orange-400" : "placeholder:text-gray-400"}`}
        />
        {isFocused &&
          (isLoading ? (
            <div className="field-spinner" />
          ) : (
            <HiOutlineSearch className="size-4 shrink-0 text-gray-400" />
          ))}
      </div>

      {showPopover && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={{ ...floatingStyles, zIndex: POPOVER_Z_INDEX }}
            className="flex flex-col"
            {...getFloatingProps()}
          >
            <div
              className="suggestion-panel"
              data-modal="true"
              onMouseMove={onPanelMouseMove}
            >
              {suggestions.length > 0 && (
                <ul role="list" className="min-h-0 flex-1 overflow-auto py-1">
                  {suggestions.map((mutuelle, index) => {
                    const isSelected = index === selectedIndex;
                    const label = mutuelle.name ? `${mutuelle.code} — ${mutuelle.name}` : mutuelle.code;
                    return (
                      <li
                        key={mutuelle._id || mutuelle.code}
                        ref={isSelected ? selectedItemRef : null}
                        onMouseDown={(event) => {
                          event.preventDefault();
                          selectMutuelle(mutuelle);
                        }}
                        onMouseEnter={() => {
                          if (isKeyboardNavigating === false) setSelectedIndex(index);
                        }}
                        className="suggestion-item"
                        data-selected={isSelected}
                      >
                        <div
                          className={`flex items-center gap-2 px-3 py-1.5 text-[13px] leading-snug ${isSelected ? "text-primary-700" : "text-gray-700"}`}
                          title={label}
                        >
                          <span className={`shrink-0 font-semibold ${isSelected ? "text-primary-700" : "text-gray-900"}`}>{mutuelle.code}</span>
                          {mutuelle.name && <span className="truncate">{mutuelle.name}</span>}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}

              {hasQuery && isLoading && suggestions.length === 0 && (
                <div className="suggestion-empty">Recherche…</div>
              )}

              {hasQuery && !isLoading && suggestions.length === 0 && footerVisible === false && (
                <div className="suggestion-empty">Aucune mutuelle trouvée</div>
              )}

              {footerVisible === true && (
                <button
                  type="button"
                  onMouseDown={(event) => {
                    event.preventDefault();
                    addManualMutuelle();
                  }}
                  onMouseEnter={() => {
                    if (isKeyboardNavigating === false) setSelectedIndex(suggestions.length);
                  }}
                  className="suggestion-footer"
                  data-selected={footerSelected}
                >
                  <span className="suggestion-footer-badge">+</span>
                  <span className="truncate">Ajouter « {inputValue.trim()} » manuellement</span>
                </button>
              )}
            </div>
          </div>
        </FloatingPortal>
      )}
    </div>
  );
};

export default MutuelleLookup;
